import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import isEqual from "lodash.isequal";
import moment from "moment";
import * as React from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import type {Result} from "@khanacademy/wonder-blocks-data";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, Caption, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useMutation, useQuery} from "@khan/data-gql";
import {RequestInfo} from "@khan/shared-core";
import {reload} from "@khan/urls";

import KhanmigoForTeachersReferralBanner from "#khanmigo-for-teachers-referrals-package/khanmigo-for-teachers-referral-banner.tsx";
import useTeacherIsEligibleForReferrals from "#khanmigo-for-teachers-referrals-package/use-teacher-is-eligible-for-referrals.ts";
import useWhamBoostReferralFlag from "#khanmigo-for-teachers-referrals-package/use-wham-boost-referral-flag.ts";

import type {DisableAIGuide} from "./__graphql-types__/DisableAIGuide.ts";
import type {EnableAIGuide} from "./__graphql-types__/EnableAIGuide.ts";
import type {getKhanLabsSettings_user_aiGuideAccessPlans as GqlAccessPlans} from "./__graphql-types__/getKhanLabsSettings.ts";
import type {
    UserPurchases,
    UserPurchases_userPurchases as UserPurchasesUserPurchases,
    UserPurchasesVariables,
} from "./__graphql-types__/UserPurchases.ts";
import {categorize, setIndex} from "./ai-guide-settings-helpers.ts";
import AIGuideTitle from "./ai-guide-title.tsx";
import AIGuideUserEntry from "./ai-guide-user-entry.tsx";
import ConfirmSubscriptionCancel from "./confirm-subscription-cancel.tsx";
import {DISABLE_AI_GUIDE_MUTATION} from "./disable-ai-guide-mutation.ts";
import {ENABLE_AI_GUIDE_MUTATION} from "./enable-ai-guide-mutation.ts";
import {ENABLE_DISABLE_CHILDREN_AI_GUIDE_MUTATION} from "./enable-disable-children-ai-guide-mutation.ts";
import {USER_PURCHASES_QUERY} from "./graphql.ts";

type Props = {
    isUnder18: boolean;
    isParent: boolean;
    primaryUser: AIGuideUser;
    childrenMaybeUnder18: Array<AIGuideUser>;
    everHadKhanmigoViaStripe: boolean;
};

export type AIGuideEnrollmentGroup = GqlAccessPlans["enrollmentGroup"];

// sync-start:ai-guide-disabled-reason 1220367462 https://github.com/Khan/webapp/blob/master/services/ai-guide/enrollment.graphql
export type AIGuideDisabledReason =
    | "DISABLED_BY_USER"
    | "DISABLED_BY_PARENT"
    | "DISABLED_BY_DISTRICT"
    | "MODERATION"
    | "LAPSED_DONATION"
    | "TRIAL_ENDED"
    | "INSUFFICIENT_DONATION"
    | "UNSUPPORTED_COUNTRY"
    | "NOT_AN_ADULT"
    | "DISCUSSION_BANNED"
    | "IS_PART_OF_A_DISTRICT"
    | "TOO_MANY_CHILDREN"
    | "CHILDREN_IN_CLASSROOM"
    | "INVALID_INVITATION_USE"
    | "DISABLED_BY_ACCESS_PLAN_MANAGER"
    | "SUBSCRIPTION_CANCELLED"
    | "ACCESS_EXPIRED"
    | "TEACHER_ROLE_REMOVED"
    | "WRITING_COACH_ASSIGNMENTS_PAST_DUE";
// sync-end:ai-guide-disabled-reason

const DEV_STRIPE_CUSTOMER_PORTAL =
    "https://checkout.khanacademy.org/p/login/test_5kA3dEgf4dqMbCM4gg";
const PROD_STRIPE_CUSTOMER_PORTAL =
    "https://checkout.khanacademy.org/p/login/5kAcOi1dwfR99pKcMM";
const AI_GUIDE_CHILD_LIMIT = 10;

const DATE_TIME_FORMAT = new Intl.DateTimeFormat("en-us", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZoneName: "short",
});

export type AIGuideUser = {
    kaid: string;
    nickname: string;
    isAIGuideEnabled: boolean;
    aiGuideEnrollmentGroup: AIGuideEnrollmentGroup;
    aiGuideDisabledReason: AIGuideDisabledReason | null | undefined;
    includesDistrictOwnedData: boolean;
    accessPlans: Array<GqlAccessPlans> | null | undefined;
};

type StateAffectingSave = {
    isSaveLoading: boolean;
    guideEnabledForParent: boolean;
    lastSavedGuideEnabledForParent: boolean;
    guideEnabledForChild: Array<boolean>;
    lastSavedGuideEnabledForChild: Array<boolean>;
};

function canSave(states: StateAffectingSave): boolean {
    if (states.isSaveLoading) {
        return false;
    }
    if (
        states.guideEnabledForParent === states.lastSavedGuideEnabledForParent
    ) {
        return !isEqual(
            states.guideEnabledForChild,
            states.lastSavedGuideEnabledForChild,
        );
    }
    return true;
}

const popSuccessToast = () => {
    import("@khan/component-toast").then(({renderChangesSavedToast}) => {
        renderChangesSavedToast();
    });
};

export default function AIGuideSettings({
    isUnder18,
    isParent,
    childrenMaybeUnder18,
    primaryUser,
    everHadKhanmigoViaStripe,
}: Props): React.ReactElement {
    const easyCancelEnabled = useFeatureIsOn("khanmigo_easy_cancel");
    const referralFlag = useWhamBoostReferralFlag();

    const teacherIsEligibleForReferrals = useTeacherIsEligibleForReferrals();

    const [disableAIGuide] = useMutation(DISABLE_AI_GUIDE_MUTATION);
    const [enableAIGuide] = useMutation(ENABLE_AI_GUIDE_MUTATION);
    const [enableDisableChildren] = useMutation(
        ENABLE_DISABLE_CHILDREN_AI_GUIDE_MUTATION,
    );
    const [
        activeNonExpiringSubscriptionId,
        setActiveNonExpiringSubscriptionId,
    ] = React.useState("");

    // This is purely used to detect a user has just canceled their
    // subscription. We hide the cancel button after they have done so
    const [subscriptionCanceled, setSubscriptionCanceled] =
        React.useState(false);
    const [subscriptionEndingDate, setSubscriptionEndingDate] =
        React.useState("");
    const [subscriptionEndedDate, setSubscriptionEndedDate] =
        React.useState("");

    const [getUserPurchases] = useQuery<UserPurchases, UserPurchasesVariables>(
        USER_PURCHASES_QUERY,
    );

    React.useEffect(() => {
        if (getUserPurchases.status === "success" && getUserPurchases.data) {
            setActiveNonExpiringSubscriptionId(
                getSubscriptionId(getUserPurchases.data.userPurchases),
            );

            // Logic for determining if the user has an ended or ending subscription
            if (getUserPurchases.data.userPurchases?.length) {
                let mostRecentEnded: UserPurchasesUserPurchases | null = null;

                for (const up of getUserPurchases.data.userPurchases.filter(
                    (up) => up?.kind === "SUBSCRIPTION",
                ) as UserPurchasesUserPurchases[]) {
                    // If there's an active subscription with no cancellation date, we don't want to show the banner
                    if (up.isActive) {
                        mostRecentEnded = null;
                        // If purchase is active AND has cancelAt, it means the subscription is ending
                        if (up.cancelAt) {
                            setSubscriptionEndingDate(
                                DATE_TIME_FORMAT.format(new Date(up.cancelAt)),
                            );
                        }
                        // Break out because there's either an active sub without
                        // a cancel date (thus no banner), or there is a cancel date
                        // and we have set the subscriptionEndingDate
                        break;
                    }

                    // If purchase is inactive and has endedAt
                    if (up.endedAt) {
                        // If we don't have an endedAt set, we set it
                        // Otherwise, we check to see if the current end is later
                        // than the most recent end
                        if (
                            mostRecentEnded === null ||
                            (mostRecentEnded.endedAt &&
                                moment(up.endedAt).isAfter(
                                    mostRecentEnded.endedAt,
                                ))
                        ) {
                            mostRecentEnded = up;
                        }
                    }
                }

                // If we have a mostRecentEnded, we want to set the subscriptionEndedDate to it
                if (mostRecentEnded?.endedAt) {
                    setSubscriptionEndedDate(
                        DATE_TIME_FORMAT.format(
                            new Date(mostRecentEnded.endedAt),
                        ),
                    );
                }
            }
        }
    }, [getUserPurchases]);

    const bannerText = React.useMemo(() => {
        if (subscriptionCanceled) {
            return t`Your Khanmigo subscription was successfully cancelled. You will have access until the end of your subscription period.`;
        }
        if (subscriptionEndedDate && !subscriptionEndingDate) {
            return t`Your Khanmigo subscription ended on ${subscriptionEndedDate}.`;
        }
        if (subscriptionEndingDate && !subscriptionEndedDate) {
            return t`Your Khanmigo subscription will end on ${subscriptionEndingDate}.`;
        }
        return "";
    }, [subscriptionCanceled, subscriptionEndedDate, subscriptionEndingDate]);

    const [guideEnabledForParent, setGuideEnabledForParent] = React.useState(
        primaryUser.isAIGuideEnabled,
    );
    const [lastSavedGuideEnabledForParent, setLastSavedGuideEnabledForParent] =
        React.useState(primaryUser.isAIGuideEnabled);

    const [isSaveLoading, setSaveLoading] = React.useState(false);
    const [hasSaveError, setHasSaveError] = React.useState(false);

    const [guideEnabledForChild, setGuideEnabledForChild] = React.useState(
        childrenMaybeUnder18.map((ch) => ch.isAIGuideEnabled),
    );
    const guideChildLimitReached = React.useMemo(() => {
        return (
            guideEnabledForChild.filter((ch) => ch === true).length >=
            AI_GUIDE_CHILD_LIMIT
        );
    }, [guideEnabledForChild]);

    const remainingChildrenForGuideLabel = React.useMemo(() => {
        const enabledChildrenCount = guideEnabledForChild.filter(
            (ch) => ch === true,
        ).length;
        if (enabledChildrenCount >= AI_GUIDE_CHILD_LIMIT) {
            return (
                <LabelSmall style={styles.note}>
                    {t`You have reached the limit for Khanmigo-enabled children (Maximum: ${AI_GUIDE_CHILD_LIMIT}).`}
                </LabelSmall>
            );
        }
        const remainingChildrenCount =
            AI_GUIDE_CHILD_LIMIT - enabledChildrenCount;
        return (
            <LabelSmall style={styles.note}>
                {t`You can enable Khanmigo for ${remainingChildrenCount} more ${remainingChildrenCount === 1 ? "child" : "children"} (Maximum: ${AI_GUIDE_CHILD_LIMIT}).`}
            </LabelSmall>
        );
    }, [guideEnabledForChild]);
    const [lastSavedGuideEnabledForChild, setLastSavedGuidEnabledForChild] =
        React.useState(guideEnabledForChild);

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const onSave = React.useCallback(async () => {
        setSaveLoading(true);
        setHasSaveError(false);

        const operation = categorize(
            guideEnabledForParent,
            lastSavedGuideEnabledForParent,
            guideEnabledForChild,
            lastSavedGuideEnabledForChild,
            primaryUser.kaid,
            childrenMaybeUnder18.map((u) => u.kaid),
        );

        const notEnrolledInDistricts = (kaid) => {
            // We want to exclude any operations that include children
            // with districts owned data
            for (const child of childrenMaybeUnder18) {
                if (child.kaid === kaid) {
                    return !child.includesDistrictOwnedData;
                }
            }

            return true;
        };

        const publishEnableDisableEvent = (
            kaid: string,
            eventType:
                | "KhanLabsSettingsAiGuideDisabled"
                | "KhanLabsSettingsAiGuideEnabled",
        ) => {
            const relationship = kaid === primaryUser.kaid ? "SELF" : "PARENT";
            publishAnalyticsEvent({
                eventType,
                eventSchemaVersion: 2,
                affectedUserKaid: kaid,
                relationship,
            });
        };

        // Enable/disable children

        const childrenToDisable = Array.from(operation.disableChildren).filter(
            notEnrolledInDistricts,
        );

        const childrenToEnable = Array.from(operation.enableChildren).filter(
            notEnrolledInDistricts,
        );

        childrenToDisable.forEach((kaid) =>
            publishEnableDisableEvent(kaid, "KhanLabsSettingsAiGuideDisabled"),
        );

        childrenToEnable.forEach((kaid) =>
            publishEnableDisableEvent(kaid, "KhanLabsSettingsAiGuideEnabled"),
        );

        const childrenOp = enableDisableChildren({
            disableKAIDs: childrenToDisable,
            enableKAIDs: childrenToEnable,
        });

        // Enable/disable parent

        let parentDisableOp: Promise<Result<DisableAIGuide>> | undefined;
        let parentEnableOp: Promise<Result<EnableAIGuide>> | undefined;

        if (operation.disableParent) {
            const parentKAID = operation.disableParent;
            publishEnableDisableEvent(
                parentKAID,
                "KhanLabsSettingsAiGuideDisabled",
            );
            parentDisableOp = disableAIGuide({kaid: parentKAID});
        }

        if (operation.enableParent) {
            const parentKAID = operation.enableParent;
            publishEnableDisableEvent(
                parentKAID,
                "KhanLabsSettingsAiGuideEnabled",
            );
            parentEnableOp = enableAIGuide({kaid: parentKAID});
        }

        // Gather results

        const childrenResult = await childrenOp;
        let parentDisableResult: Result<DisableAIGuide> | undefined;
        let parentEnableResult: Result<EnableAIGuide> | undefined;

        if (parentDisableOp) {
            parentDisableResult = await parentDisableOp;
        }

        if (parentEnableOp) {
            parentEnableResult = await parentEnableOp;
        }

        const childrenSucceeded =
            childrenResult.status === "success" &&
            childrenResult.data.aiGuideActivateChildAccounts?.errors &&
            childrenResult.data.aiGuideActivateChildAccounts.errors.length ===
                0 &&
            childrenResult.data.aiGuideDeactivateChildAccounts?.errors &&
            childrenResult.data.aiGuideDeactivateChildAccounts.errors.length ===
                0;

        const parentDisableSucceeded = parentDisableResult
            ? parentDisableResult.status === "success" &&
              !parentDisableResult.data.disableAIGuide?.error
            : true;

        const parentEnableSucceeded = parentEnableResult
            ? parentEnableResult.status === "success" &&
              !parentEnableResult.data.enableAIGuide?.error
            : true;

        if (
            childrenSucceeded &&
            parentEnableSucceeded &&
            parentDisableSucceeded
        ) {
            if (guideEnabledForParent !== lastSavedGuideEnabledForParent) {
                // Reload to force AI guide to pick up state change, if it
                // affects the current user.
                reload();
            }
            setLastSavedGuideEnabledForParent(guideEnabledForParent);
            setLastSavedGuidEnabledForChild(guideEnabledForChild);
            setSaveLoading(false);
            popSuccessToast();
        } else {
            setSaveLoading(false);
            setHasSaveError(true);
        }
    }, [
        childrenMaybeUnder18,
        lastSavedGuideEnabledForChild,
        lastSavedGuideEnabledForParent,
        setSaveLoading,
        setHasSaveError,
        enableAIGuide,
        disableAIGuide,
        primaryUser,
        guideEnabledForParent,
        guideEnabledForChild,
        publishAnalyticsEvent,
        enableDisableChildren,
    ]);

    const anyChildIsDisabledDueToModeration = childrenMaybeUnder18.some(
        (ch) => ch.aiGuideDisabledReason === "MODERATION",
    );

    // TODO: is this still relevant? 1) The system currently does not
    // disable children when a parent's subscription lapses. 2) LAPSED_DONATION
    // was only used with Classy-based subscriptions.
    const anyChildIsDisabledDueToLapsedDonation = childrenMaybeUnder18.some(
        (ch) => ch.aiGuideDisabledReason === "LAPSED_DONATION",
    );

    // TODO: is this still relevant? 1) The system currently does not
    // disable children when a parent's subscription lapses. 2) INSUFFICIENT_DONATION
    // was only used with Classy-based subscriptions.
    const anyChildIsDisabledDueToInsufficientDonation =
        childrenMaybeUnder18.some(
            (ch) => ch.aiGuideDisabledReason === "INSUFFICIENT_DONATION",
        );

    const anyChildHasDistrictOwnedData = childrenMaybeUnder18.some(
        (ch) => ch.includesDistrictOwnedData,
    );

    const hasManagedChildren = childrenMaybeUnder18.length > 0;

    const canEnableChildren = ((): boolean => {
        if (!primaryUser.aiGuideEnrollmentGroup) {
            return false;
        }

        // the TEACHERS_FREE access is applied via ACCESS_PLAN_MANAGER and not
        // directly on the AiGuideEnrollment model. If the user has a different
        // enrollmentGroup on the AiGuideEnrollment model, that means they
        // likely, though not definitely, have some sort of access that would
        // allow them the ability to enable Khanmigo for their children
        // because, currently, the TEACHERS_FREE access type is the only access
        // type that doesn't have that capability. Since there's a lack of
        // reliability here, we can't be completely certain whether these users
        // are solely TEACHERS_FREE or not, so we're going to err on the side
        // of allowing them to enable their children so that we're not taking
        // away functionality they've been using.
        if (primaryUser.aiGuideEnrollmentGroup !== "ACCESS_PLAN_MANAGER") {
            return true;
        }

        return !!primaryUser.accessPlans?.some(
            (plan) => plan.isActive && plan.enrollmentGroup !== "TEACHERS_FREE",
        );
    })();

    const manageDonationUi = (
        <View>
            {!!activeNonExpiringSubscriptionId && (
                <Strut size={spacing.medium_16} />
            )}

            {/* User currently has Khanmigo */}
            {!subscriptionCanceled && !!activeNonExpiringSubscriptionId && (
                <>
                    {/* Easy cancel is controlled via a flag */}
                    {easyCancelEnabled && (
                        <>
                            <ModalLauncher
                                modal={({closeModal}) => (
                                    <ConfirmSubscriptionCancel
                                        onNo={() => {
                                            closeModal();
                                        }}
                                        onYes={() => {
                                            setSubscriptionCanceled(true);
                                            // No need to close the modal here because
                                            // setSubscriptionCanceled causes a
                                            // re-render
                                        }}
                                        subscriptionId={
                                            activeNonExpiringSubscriptionId
                                        }
                                    />
                                )}
                            >
                                {({openModal}) => (
                                    <Button
                                        onClick={openModal}
                                        style={styles.cancelSubscription}
                                    >
                                        {t`Cancel Khanmigo Subscription`}
                                    </Button>
                                )}
                            </ModalLauncher>
                            <Strut size={spacing.medium_16} />
                        </>
                    )}
                    <Body>
                        <Link
                            href={
                                RequestInfo.IS_DEV_SERVER
                                    ? DEV_STRIPE_CUSTOMER_PORTAL
                                    : PROD_STRIPE_CUSTOMER_PORTAL
                            }
                            target="_blank"
                        >
                            {RequestInfo.IS_DEV_SERVER
                                ? t`Manage your subscription on Stripe (TEST).`
                                : t`Manage your subscription on Stripe.`}
                        </Link>
                    </Body>
                    <LabelSmall>
                        {t`Update payment details or cancel your subscription.`}
                    </LabelSmall>
                </>
            )}

            {/* User used to have Khanmigo via Stripe */}
            {(subscriptionCanceled ||
                (everHadKhanmigoViaStripe &&
                    !activeNonExpiringSubscriptionId)) && (
                <>
                    <Body>
                        <Link
                            href={
                                RequestInfo.IS_DEV_SERVER
                                    ? DEV_STRIPE_CUSTOMER_PORTAL
                                    : PROD_STRIPE_CUSTOMER_PORTAL
                            }
                            target="_blank"
                        >
                            {RequestInfo.IS_DEV_SERVER
                                ? t`Manage your account on Stripe (TEST).`
                                : t`Manage your account on Stripe.`}
                        </Link>
                    </Body>
                    <LabelSmall>
                        {t`View invoices, update customer info and payment methods.`}
                    </LabelSmall>
                </>
            )}
            {bannerText && (
                <View style={[styles.banner]}>
                    <Banner text={bannerText} kind="info" layout="floating" />
                </View>
            )}
        </View>
    );

    if (primaryUser.aiGuideDisabledReason === "MODERATION") {
        return (
            <View role="region" aria-label={t`Account moderation status`}>
                <AIGuideTitle />
                <Strut size={spacing.medium_16} />
                <Banner
                    text={
                        <Trans>
                            Your Khanmigo access was disabled by our moderation
                            system. For more information please review our{" "}
                            <Link href="/r/khanmigo-moderation">
                                community guidelines
                            </Link>
                            .
                        </Trans>
                    }
                    kind="warning"
                    layout="full-width"
                    aria-label="Moderation warning banner"
                />
                <Strut size={spacing.large_24} />
                {manageDonationUi}
            </View>
        );
    }

    // TODO: is this still relevant? LAPSED_DONATION and INSUFFICIENT_DONATION
    // were only used with Classy-based subscriptions. Does this need
    // to be replaced by or augmented with examination of USER_PURCHASE
    // access plans?
    if (
        primaryUser.aiGuideDisabledReason === "LAPSED_DONATION" ||
        primaryUser.aiGuideDisabledReason === "INSUFFICIENT_DONATION"
    ) {
        return (
            <View role="region" aria-label={t`Subscription status`}>
                <AIGuideTitle />
                <Strut size={spacing.medium_16} />
                {!!activeNonExpiringSubscriptionId &&
                primaryUser.aiGuideDisabledReason === "LAPSED_DONATION" ? (
                    <Banner
                        text={
                            <Trans>
                                Your Khanmigo access was disabled because your
                                subscription has lapsed.
                            </Trans>
                        }
                        kind="warning"
                        layout="full-width"
                        aria-label="Subscription warning banner"
                    />
                ) : (
                    <Banner
                        text={
                            <Trans>
                                Your Khanmigo access was disabled either because
                                your donation has lapsed or because the donation
                                does not meet the required amount.
                            </Trans>
                        }
                        kind="warning"
                        layout="full-width"
                        aria-label="Donation warning banner"
                    />
                )}
                <Strut size={spacing.large_24} />
                {manageDonationUi}
            </View>
        );
    }

    // TODO: is this notice still desired? If so, it needs to be
    // replaced with or augmented by examination of a user's access
    // plans. Although there is a general-purpose enrollment group
    // for free trials (invitation-based-free-trial) there are also
    // several dedicated enrollment groups for specific trials. Should
    // this be updated to display the message to all of them?
    if (primaryUser.aiGuideDisabledReason === "TRIAL_ENDED") {
        return (
            <View role="region" aria-label={t`Trial status`}>
                <AIGuideTitle />
                <Strut size={spacing.medium_16} />
                <Banner
                    text={
                        <Trans>
                            Your free trial of Khanmigo has ended. If you would
                            like access to Khanmigo, please{" "}
                            <Link target="_blank" href="https://khanmigo.ai">
                                sign up and start a subscription
                            </Link>
                            .
                        </Trans>
                    }
                    kind="warning"
                    layout="full-width"
                    aria-label="Subscription warning banner"
                />
                <Strut size={spacing.large_24} />
                {manageDonationUi}
            </View>
        );
    }

    return (
        <View role="region" aria-label={t`AI Guide settings`}>
            <AIGuideTitle />
            <Strut size={spacing.medium_16} />
            {anyChildIsDisabledDueToModeration && (
                <React.Fragment>
                    <Banner
                        text={
                            <Trans>
                                One or more of your children had their Khanmigo
                                access disabled by our moderation system. For
                                more information please review our{" "}
                                <Link href="/r/khanmigo-moderation">
                                    community guidelines
                                </Link>
                                .
                            </Trans>
                        }
                        kind="warning"
                        layout="full-width"
                        aria-label="Moderation warning banner"
                    />
                    <Strut size={spacing.medium_16} />
                </React.Fragment>
            )}
            {anyChildIsDisabledDueToLapsedDonation ||
                (anyChildIsDisabledDueToInsufficientDonation &&
                    (activeNonExpiringSubscriptionId ? (
                        <React.Fragment>
                            <Banner
                                text={
                                    <Trans>
                                        The child accounts associated with your
                                        account have had Khanmigo access
                                        disabled because your subscription has
                                        lapsed.
                                    </Trans>
                                }
                                kind="warning"
                                layout="full-width"
                                aria-label="Subscription warning banner"
                            />
                            <Strut size={spacing.medium_16} />
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <Banner
                                text={
                                    <Trans>
                                        The child accounts associated with your
                                        account have had Khanmigo access
                                        disabled because your donation has
                                        lapsed or does not meet the required
                                        amount.
                                    </Trans>
                                }
                                kind="warning"
                                layout="full-width"
                                aria-label="Moderation warning banner"
                            />
                            <Strut size={spacing.medium_16} />
                        </React.Fragment>
                    )))}
            {/* @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"DISABLED_BY_USER" | "DISABLED_BY_DISTRICT" | "UNSUPPORTED_COUNTRY" | "NOT_AN_ADULT" | "TOO_MANY_CHILDREN" | "CHILDREN_IN_CLASSROOM" | "INVALID_INVITATION_USE" | null | undefined' and '"LAPSED_DONATION"' have no overlap. */}
            {primaryUser.aiGuideDisabledReason === "LAPSED_DONATION" ||
                // @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"DISABLED_BY_USER" | "DISABLED_BY_DISTRICT" | "UNSUPPORTED_COUNTRY" | "NOT_AN_ADULT" | "TOO_MANY_CHILDREN" | "CHILDREN_IN_CLASSROOM" | "INVALID_INVITATION_USE" | null | undefined' and '"INSUFFICIENT_DONATION"' have no overlap.
                (primaryUser.aiGuideDisabledReason ===
                    "INSUFFICIENT_DONATION" &&
                    (activeNonExpiringSubscriptionId ? (
                        <React.Fragment>
                            <Banner
                                text={
                                    <Trans>
                                        Khanmigo has been disabled for you and
                                        all your children because your
                                        subscription has lapsed.
                                    </Trans>
                                }
                                kind="warning"
                                layout="full-width"
                                aria-label="Subscription warning banner"
                            />
                            <Strut size={spacing.medium_16} />
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <Banner
                                text={
                                    <Trans>
                                        Khanmigo has been disabled for you and
                                        all your children because your donation
                                        has lapsed or does not meet the required
                                        amount.
                                    </Trans>
                                }
                                kind="warning"
                                layout="full-width"
                                aria-label="Moderation warning banner"
                            />
                            <Strut size={spacing.medium_16} />
                        </React.Fragment>
                    )))}
            {hasManagedChildren && (
                <React.Fragment>
                    <Body>
                        <Trans>
                            Learn more about{" "}
                            <Link
                                href="https://support.khanacademy.org/hc/en-us/articles/14394695651597"
                                target="_blank"
                                inline={true}
                            >
                                Enabling Khanmigo for your children
                            </Link>
                            .
                        </Trans>
                    </Body>
                    <Strut size={spacing.medium_16} />
                </React.Fragment>
            )}
            {isParent && (
                <Body>
                    {t`Disabling Khanmigo for yourself will disable the experience for all children managed by the account.`}
                </Body>
            )}
            {anyChildHasDistrictOwnedData && (
                <LabelSmall style={styles.note}>
                    {t`Note: if your child's account is enrolled in a Khan Academy District, you will not be able to make changes to the Khanmigo enrollment status for that account.`}
                </LabelSmall>
            )}
            {guideEnabledForChild.length > 0 &&
                guideEnabledForParent &&
                remainingChildrenForGuideLabel}
            <Strut size={spacing.large_24} />
            <Checkbox
                checked={guideEnabledForParent}
                disabled={isUnder18}
                onChange={setGuideEnabledForParent}
                label={t`Enable Khanmigo for yourself.`}
            />
            {childrenMaybeUnder18.length > 0 && !canEnableChildren && (
                <React.Fragment>
                    <Strut size={spacing.medium_16} />
                    <Body style={styles.note}>
                        <Trans>
                            Your current Khanmigo plan does not allow you to
                            enable Khanmigo access for your children.{" "}
                            <Link
                                href="https://khanmigo.ai/parents"
                                target="_blank"
                                inline={true}
                            >
                                Purchase a Khanmigo plan that includes child
                                accounts.
                            </Link>
                        </Trans>
                    </Body>
                </React.Fragment>
            )}
            <View>
                {childrenMaybeUnder18.map((ch, i) => (
                    <React.Fragment key={i}>
                        <Strut size={spacing.medium_16} />
                        <AIGuideUserEntry
                            nickname={ch.nickname}
                            isDisabledDueToModeration={
                                ch.aiGuideDisabledReason === "MODERATION"
                            }
                            isAIGuideEnabled={guideEnabledForChild[i]}
                            setAIGuideEnabled={(value: boolean) =>
                                setGuideEnabledForChild(
                                    setIndex(value, i, guideEnabledForChild),
                                )
                            }
                            isAIGuideEnabledForParent={guideEnabledForParent}
                            isChild={true}
                            includesDistrictOwnedData={
                                ch.includesDistrictOwnedData
                            }
                            canEnableChildren={canEnableChildren}
                            guideChildLimitReached={guideChildLimitReached}
                        />
                    </React.Fragment>
                ))}
            </View>
            <Strut size={spacing.xLarge_32} />
            {manageDonationUi}
            <Strut size={spacing.xLarge_32} />
            {!isUnder18 && (
                <View style={styles.buttonContainer}>
                    <Button
                        onClick={onSave}
                        style={styles.button}
                        disabled={
                            !canSave({
                                isSaveLoading,
                                guideEnabledForParent,
                                lastSavedGuideEnabledForParent,
                                guideEnabledForChild,
                                lastSavedGuideEnabledForChild,
                            })
                        }
                        testId="ai-guide-settings-save-button"
                    >
                        {t`Save changes`}
                    </Button>
                </View>
            )}
            <div area-live="polite">
                {hasSaveError && (
                    <Caption style={styles.error}>
                        {t`Failed to save changes`}
                    </Caption>
                )}
            </div>
            {referralFlag && teacherIsEligibleForReferrals && (
                <>
                    <Strut size={spacing.xLarge_32} />
                    <KhanmigoForTeachersReferralBanner
                        hideImage={true}
                        headerText={t`Khanmigo Referrals`}
                    />
                </>
            )}
            <Strut size={spacing.medium_16} />
        </View>
    );
}

// extracts a subscription id from a list of access plans
function getSubscriptionId(
    ups: (UserPurchasesUserPurchases | null | undefined)[] | null | undefined,
): string {
    if (!ups) {
        return "";
    }

    for (const up of ups) {
        // We also check cancelAtPeriodEnd because a subscription could be
        // active, yet set to expire at the end of the billing period.
        if (
            up?.kind === "SUBSCRIPTION" &&
            up.isActive &&
            !up.cancelAtPeriodEnd
        ) {
            return up.extId;
        }
    }

    return "";
}

const styles = StyleSheet.create({
    button: {
        alignSelf: "flex-start",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
    buttonContainer: {
        flexDirection: "row",
    },
    error: {
        color: color.red,
        height: "100%",
        marginLeft: spacing.small_12,
        marginTop: 10,
    },
    note: {
        fontStyle: "italic",
    },
    cancelSubscription: {
        width: "50%",
        minWidth: 300,
    },
    banner: {
        marginTop: 10,
    },
});
